/*
 * Marketing > Keyword Rankings tab content.
 *
 * Ranks and volumes are SEED ESTIMATES from a manual research pass (last checked 5 Nov 2026),
 * not a live SERP feed. Re-check in an incognito Google UK search and update ourRank / lastChecked.
 *
 * Task keys (seo-<keywordSlug>-<actionSlug>) are stored by the roadmap endpoints.
 * Never rename an id once in use, or its tick and comments will no longer match.
 */

export const OUR_SITE = "https://11plusmagic.co.uk/";
export const LAST_CHECKED = "2026-11-05";

export const INTENTS = [
  { id: "informational", label: "Informational", color: "#2563eb" },
  { id: "commercial", label: "Commercial", color: "#16a34a" },
  { id: "branded", label: "Branded", color: "#7c3aed" },
];

const a = (id, title, detail, effort) => ({ id, title, detail, ...(effort ? { effort } : {}) });

const kw = (fields) => ({
  ourUrl: OUR_SITE,
  lastChecked: LAST_CHECKED,
  rankSource: "estimate",
  ourRank: null,
  ...fields,
});

export const KEYWORDS = [
  kw({
    id: "practice-papers",
    keyword: "11 plus practice papers",
    volume: 2000,
    volumeNote: "~2,000/mo UK (estimate)",
    intent: "commercial",
    priority: 1,
    leader: { name: "CGP", site: "cgpbooks.co.uk", note: "CGP, Eleven Plus Exams and Bond (Oxford) own page one with product and free-paper pages." },
    gap: "We have no dedicated practice-papers landing page; the home page has to compete with publisher catalogues.",
    actions: [
      a("landing-page", "Build a /11-plus-practice-papers landing page", "H1 with the exact phrase, GL and CEM sections, sample questions and a free-test CTA.", "1 day"),
      a("free-sample", "Offer a free downloadable sample paper", "Gated by email; link it from the landing page to earn links and capture leads.", "half day"),
      a("schema", "Add FAQ and Product schema", "FAQ on paper formats, timings and marking; check in Rich Results Test."),
      a("internal-links", "Link to the page from home, blog and app pages", "Use the exact anchor text in at least five internal links."),
      a("outreach", "Pitch the page to 5 parent blogs / school PTAs", "Ask for a resource-list link; track replies in the comments."),
    ],
  }),
  kw({
    id: "exam-papers",
    keyword: "11 plus exam papers",
    volume: 1600,
    volumeNote: "~1,000–2,000/mo (estimate)",
    intent: "commercial",
    priority: 1,
    leader: { name: "Eleven Plus Exams", site: "elevenplusexams.co.uk", note: "Strong forum + shop authority; CGP and Bond also rank." },
    gap: "Not ranking; past-paper style searches go to publishers and school sites.",
    actions: [
      a("section", "Add an exam-papers section to the practice-papers page", "Explain that real past papers are not published and what our mocks mirror."),
      a("by-region", "List which regions use GL vs CEM papers", "Table by region linking to our Kent / GL / CEM pages."),
      a("title-meta", "Write title and meta for 'exam papers'", "Include 'free' and 'GL & CEM style' in the meta description."),
    
      a("internal-links", "Add internal links from home and regional pages", "Exact-match anchors to the exam-papers section."),
      a("outreach", "Ask 3 parent blogs to link the exam-papers section", "After the landing page is live; track replies in comments."),],
  }),
  kw({
    id: "free-papers",
    keyword: "free 11 plus papers",
    volume: 1300,
    volumeNote: "~1,000+/mo (estimate)",
    intent: "commercial",
    priority: 1,
    leader: { name: "Eleven Plus Exams", site: "elevenplusexams.co.uk", note: "Free paper downloads; school sites (e.g. consortium pages) also rank." },
    gap: "Free test exists in the app but has no indexable page.",
    actions: [
      a("free-test-page", "Create a /free-11-plus-test page", "Indexable page describing the free test, with a sign-up CTA and sample questions in HTML.", "1 day"),
      a("pdf-sample", "Publish one free PDF sample per subject", "Maths, English, VR, NVR; each with answers."),
      a("ads-landing", "Point free-entry ads at the new page", "Re-use the 'Try a Free 11+ Practice Test' ad copy from the Action Plan."),
      a("backlinks", "Submit to free-resource roundups", "Find 'free 11 plus resources' list posts and request inclusion."),
    
      a("schema", "Add FAQ schema on the free-test page", "Cover formats, timing and marking; validate in Rich Results Test."),],
  }),
  kw({
    id: "mock-exams",
    keyword: "11 plus mock exams",
    volume: 1000,
    volumeNote: "~1,000/mo, seasonal peak Jun–Sep (estimate)",
    intent: "commercial",
    priority: 2,
    leader: { name: "Atom Learning", site: "atomlearning.co.uk", note: "Atom and local mock-exam providers dominate; EPP runs mock events." },
    gap: "Our online timed mocks are not described anywhere Google can see.",
    actions: [
      a("mock-page", "Publish an online mock exams page", "Timed, auto-marked, GL/CEM style; screenshots of results."),
      a("calendar", "Add a mock-exam season calendar", "Suggested mock dates before Sept tests by region."),
      a("reviews", "Collect 3 parent quotes about mocks", "Add as testimonials with Review schema only if genuine."),
    
      a("schema", "Add FAQ and Service schema on the mocks page", "Include GL/CEM style and auto-marking questions."),
      a("outreach", "Pitch the mocks page to 5 PTA / parent blogs", "Offer a free mock code for readers."),],
  }),
  kw({
    id: "verbal-reasoning",
    keyword: "11 plus verbal reasoning",
    volume: 1000,
    volumeNote: "~1,000/mo (estimate)",
    intent: "informational",
    priority: 2,
    leader: { name: "Bond", site: "bond11plus.co.uk", note: "Bond and CGP books plus Eleven Plus Exams guides." },
    gap: "No subject guide pages.",
    actions: [
      a("guide", "Write a verbal reasoning question-types guide", "All 21 GL question types with one worked example each.", "1–2 days"),
      a("video", "Record 3 short VR explainer videos", "Embed on the guide and post to YouTube Shorts."),
      a("practice-cta", "Add a VR practice CTA to the guide", "Deep link to VR practice in the app."),
    
      a("internal-links", "Link the VR guide from home, practice papers and GL pages", "Use '11 plus verbal reasoning' anchors."),
      a("guest-post", "Pitch a VR question-types guest post to 3 education blogs", "Bio link back to the VR guide."),],
  }),
  kw({
    id: "non-verbal-reasoning",
    keyword: "11 plus non verbal reasoning",
    volume: 900,
    volumeNote: "~800–1,000/mo (estimate)",
    intent: "informational",
    priority: 2,
    leader: { name: "CGP", site: "cgpbooks.co.uk", note: "CGP and Bond books; Atom guides." },
    gap: "No NVR content on the site.",
    actions: [
      a("guide", "Write an NVR question-types guide", "Sequences, odd-one-out, analogies, codes, cubes; image examples."),
      a("image-alt", "Add descriptive alt text to every NVR image", "Helps image search."),
      a("printable", "Offer a printable NVR warm-up sheet", "Email-gated download."),
    
      a("title-meta", "Write title and meta for '11 plus non verbal reasoning'", "Include 'question types' and 'worked examples'."),
      a("outreach", "Offer the printable NVR sheet to 5 resource roundups", "Ask for a link to the NVR guide."),],
  }),
  kw({
    id: "maths-practice",
    keyword: "11 plus maths practice",
    volume: 700,
    volumeNote: "~500–1,000/mo (estimate)",
    intent: "commercial",
    priority: 3,
    leader: { name: "CGP", site: "cgpbooks.co.uk", note: "CGP, Bond and Atom rank; YouTube tutors appear in video results." },
    gap: "Not ranking.",
    actions: [
      a("topic-page", "Create a maths practice page by topic", "Fractions, ratio, algebra, word problems; link to app practice."),
      a("worked-examples", "Add 10 worked examples in HTML", "Text, not images, so Google can read them."),
      a("youtube", "Publish 5 maths Shorts linking back", "Put the page URL in each description."),
    
      a("schema", "Add FAQ schema for common 11+ maths questions", "Fractions, ratio and timing FAQs."),
      a("backlinks", "Submit the maths page to free-resource roundups", "Personalised request; log replies."),],
  }),
  kw({
    id: "english-practice",
    keyword: "11 plus english practice",
    volume: 600,
    volumeNote: "~500/mo (estimate)",
    intent: "commercial",
    priority: 3,
    leader: { name: "CGP", site: "cgpbooks.co.uk", note: "CGP and Bond; Exam Happy for creative writing." },
    gap: "Not ranking.",
    actions: [
      a("topic-page", "Create an English practice page", "Comprehension, SPaG, vocabulary, cloze; sample passage."),
      a("vocab-list", "Publish a free 11+ vocabulary list", "Linkable asset; refresh yearly."),
      a("creative-writing", "Add a creative writing tips section", "Counter Exam Happy's strength here."),
    
      a("internal-links", "Link the English page from vocab list and creative writing", "Keep anchors natural."),
      a("guest-post", "Pitch a creative-writing tips guest article", "Counter Exam Happy; link the English hub."),],
  }),
  kw({
    id: "online-practice",
    keyword: "11 plus online practice / tests",
    volume: 800,
    volumeNote: "~500–1,000/mo combined (estimate)",
    intent: "commercial",
    priority: 1,
    leader: { name: "Atom Learning", site: "atomlearning.co.uk", note: "Atom, Eleven Ace and EPP compete; this is our core product category." },
    gap: "Our core product, but the home page copy does not target 'online' phrases.",
    actions: [
      a("home-copy", "Rewrite home page H1/H2 around online practice", "Include 'online 11 plus practice tests' naturally.", "half day"),
      a("comparison", "Publish an honest 'online vs books' comparison", "Mention CGP/Bond books fairly; position the app for adaptive practice."),
      a("app-store-link", "Link app store listings to the site", "Consistent naming between stores and site."),
      a("speed", "Fix Core Web Vitals on the home page", "Check PageSpeed Insights mobile; target LCP < 2.5s."),
    
      a("schema", "Add SoftwareApplication schema on the home/app page", "Platforms, free trial, offers."),
      a("backlinks", "Ask comparison / 'best apps' authors for an update", "Send a free account and canonical URL."),],
  }),
  kw({
    id: "tuition",
    keyword: "11 plus tuition / online tuition",
    volume: 1500,
    volumeNote: "~1,000–2,000/mo combined (estimate)",
    intent: "commercial",
    priority: 2,
    leader: { name: "Atom Learning", site: "atomlearning.co.uk", note: "Tutor marketplaces and local tutors dominate; Atom and EPP rank for online." },
    gap: "Our live classes / batches are not described as tuition on the site.",
    actions: [
      a("tuition-page", "Create an online 11 plus tuition page", "Batch sizes, schedule, tutors, price; clear booking CTA."),
      a("gbp", "Set up / complete a Google Business Profile", "Category 'Tutoring service'; service area UK."),
      a("faq", "Add a tuition FAQ", "Cost, group size, how online classes work."),
    
      a("schema", "Add FAQ and LocalBusiness/Service schema", "Cost, group size, online format."),
      a("citations", "Add consistent NAP citations on 5 education directories", "Match the Google Business Profile."),],
  }),
  kw({
    id: "kent",
    keyword: "kent 11 plus",
    volume: 2000,
    volumeNote: "~1,000–3,000/mo, spikes Sep/Oct (estimate)",
    intent: "informational",
    priority: 1,
    leader: { name: "Kent County Council", site: "kent.gov.uk", note: "Official council pages first; Eleven Plus Exams forum threads next." },
    gap: "No regional pages at all.",
    actions: [
      a("region-page", "Build a Kent Test guide page", "Format, dates, pass scores explained, grammar schools list; link to our practice.", "1 day"),
      a("dates", "Add key Kent Test dates for 2027 entry", "Cite kent.gov.uk; update every year."),
      a("forum-answers", "Answer Kent threads on Eleven Plus Exams forum", "Helpful answers; link only where allowed."),
    
      a("schema", "Add FAQ schema for Kent Test dates and format", "Cite kent.gov.uk in answers."),
      a("outreach", "Ask 3 Kent PTA or parent blogs to list the guide", "Resource-list link only."),],
  }),
  kw({
    id: "gl-assessment",
    keyword: "gl assessment 11 plus",
    volume: 900,
    volumeNote: "~500–1,000/mo (estimate)",
    intent: "informational",
    priority: 2,
    leader: { name: "GL Assessment", site: "gl-assessment.co.uk", note: "Official site first; Bond (GL-aligned) and CGP next." },
    gap: "Not ranking.",
    actions: [
      a("guide", "Write a GL Assessment 11+ guide", "Which areas use GL, subjects, format, our GL-style practice."),
      a("compare", "Add a GL vs CEM comparison table", "Link both guides to each other."),
      a("regions", "List GL regions with links to region pages", "Grows into future region pages."),
    
      a("internal-links", "Link the GL guide from CEM, Kent and practice hubs", "Bidirectional GL vs CEM links."),
      a("backlinks", "Pitch the GL guide to 5 tutor resource pages", "Ask for a dofollow resources link."),],
  }),
  kw({
    id: "cem",
    keyword: "cem 11 plus",
    volume: 700,
    volumeNote: "~500–1,000/mo (estimate)",
    intent: "informational",
    priority: 3,
    leader: { name: "Eleven Plus Exams", site: "elevenplusexams.co.uk", note: "Guides and forum; CGP CEM books." },
    gap: "Not ranking.",
    actions: [
      a("guide", "Write a CEM 11+ guide", "Note CEM changes (CEM Select / regions moving to GL); cite sources."),
      a("update-note", "Add a 'last updated' note to the guide", "Freshness matters for exam-format queries."),
      a("faq", "Answer 'is CEM still used?' as an FAQ", "Short, sourced answer aimed at the People Also Ask box."),
    
      a("title-meta", "Write title and meta for 'cem 11 plus'", "Include 'still used?' and 'vs GL'."),
      a("outreach", "Share the CEM update note with 5 parent blogs", "Timely format-change angle."),],
  }),
  kw({
    id: "grammar-entrance",
    keyword: "grammar school entrance exam",
    volume: 600,
    volumeNote: "~500/mo (estimate)",
    intent: "informational",
    priority: 3,
    leader: { name: "Eleven Plus Exams", site: "elevenplusexams.co.uk", note: "School lists; Wikipedia and council pages." },
    gap: "Not ranking.",
    actions: [
      a("explainer", "Write a 'what is the 11 plus' explainer", "Plain-English for new parents; link to subject guides."),
      a("school-finder", "Link to the Target Schools insight as a public list", "Only school names and exam types; no student data."),
      a("timeline", "Add a Year 4–6 preparation timeline", "Good for featured snippets."),
    
      a("schema", "Add FAQ schema on the 'what is the 11 plus' page", "Aim at People Also Ask."),
      a("directory", "List the explainer on 5 education start-page directories", "Skip paid link farms."),],
  }),
  kw({
    id: "prep-app",
    keyword: "11 plus preparation app / best 11 plus app",
    volume: 400,
    volumeNote: "~200–500/mo combined (estimate)",
    intent: "commercial",
    priority: 1,
    leader: { name: "Atom Learning", site: "atomlearning.co.uk", note: "Atom, Eleven Ace and listicles ('best 11 plus apps') rank." },
    gap: "Small volume but high intent; we are an app and should win this.",
    actions: [
      a("app-page", "Create an app landing page with store badges", "iOS, Android, web; screenshots and the free trial."),
      a("listicles", "Get included in 'best 11 plus apps' articles", "Email authors of the top 5 results with a free account."),
      a("store-aso", "Align App Store / Play titles with the phrase", "e.g. '11 Plus Magic – 11+ Practice'."),
      a("reviews", "Ask happy parents for store reviews", "In-app prompt after a good mock score."),
    
      a("schema", "Add SoftwareApplication schema on the app landing page", "Store badges and free trial."),],
  }),
  kw({
    id: "brand",
    keyword: "11 plus magic",
    volume: 50,
    volumeNote: "<100/mo (estimate)",
    intent: "branded",
    priority: 1,
    leader: { name: "11 Plus Magic", site: "11plusmagic.co.uk", note: "Should be us; check that EdTech-Ltd or app stores do not outrank the site." },
    ourRank: 1,
    gap: "Probably #1, but sitelinks, Knowledge Panel and social profiles are not set up.",
    actions: [
      a("search-console", "Verify the site in Google Search Console", "Submit sitemap; check branded impressions."),
      a("org-schema", "Add Organization schema with sameAs links", "Link Facebook, Instagram, YouTube, app stores."),
      a("social-names", "Use '11 Plus Magic' consistently on all profiles", "The Facebook page is currently EdTech-Ltd."),
    
      a("sitemap-submit", "Submit the XML sitemap after core landers ship", "Confirm indexed URL count in Search Console."),
      a("backlink-profile", "Baseline referring domains for 11plusmagic.co.uk", "Compare to Atom / EPP; note in SEO Origin & Backlinks tab."),],
  }),
];

export const PRIORITY_LABELS = { 1: "High", 2: "Medium", 3: "Low" };

export function sortKeywords(list = KEYWORDS) {
  return [...list].sort((x, y) => x.priority - y.priority || y.volume - x.volume);
}

/** "top3" | "top10" | "ranked" | "unranked" */
export function rankBand(rank) {
  if (rank === null || rank === undefined) return "unranked";
  if (rank <= 3) return "top3";
  if (rank <= 10) return "top10";
  return "ranked";
}

export function rankLabel(rank) {
  return rank === null || rank === undefined ? "Not in top 100" : `#${rank}`;
}

export function rankColor(rank) {
  const band = rankBand(rank);
  if (band === "top3") return "#047857";
  if (band === "top10") return "#b45309";
  return "#64748b";
}

export function keywordTaskId(keywordId, actionId) {
  return `seo-${keywordId}-${actionId}`;
}

export function flattenKeywordActions(list = KEYWORDS) {
  return list.flatMap((k) => k.actions.map((act) => ({
    ...act,
    id: keywordTaskId(k.id, act.id),
    actionSlug: act.id,
    keywordId: k.id,
    keyword: k.keyword,
    intent: k.intent,
  })));
}

export function rankSummary(list = KEYWORDS) {
  const bands = list.map((k) => rankBand(k.ourRank));
  return {
    total: list.length,
    top3: bands.filter((b) => b === "top3").length,
    top10: bands.filter((b) => b === "top10" || b === "top3").length,
    unranked: bands.filter((b) => b === "unranked").length,
  };
}
